import {
  Box, Button, Grid, Stack,
} from '@mui/material';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useToggle from '../../hooks/useToggle';
import { FeatureLabelModal } from '../../modules/FeatureManager/components/FeatureLabelModal';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { KPIPreview } from '../../modules/FeatureManager/components/KPIPreview';
import { SearchBar, SelectBox } from '../../components/FormComponents';
import {
  getAllAccountStats,
  getTaxonomyDetails, getTaxonomyList, getTaxonomyReport,
} from '../../features';
import { PackageHeader } from '../../components/CustomHeading';
import { TITLES } from '../../utils/constants';
import useStyles from '../../hooks/useStyles';
import styles from '../../layouts/pages/style';
import TaxonomyViewer from './TaxonomyViewer';
import FeatureTaxonomyTable from './FeatureTaxonomyTable';
import useFeatureTaxonomyTable from './hooks/useFeatureTaxonomyTable';

const viewOptionList = [
  { id: 1, name: 'Weekly', value: 'week' },
  { id: 2, name: 'Monthly', value: 'month' },
];

const defaultOption = { id: 0, name: 'All Features', value: 0 };

function FeatureTaxonomy({ permissions }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State
  const [selectedView, setSelectedView] = useState(0);
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [taxonomyOptions, setTaxonomyOptions] = useState([defaultOption]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [viewOption, setViewOption] = useState('month');
  const [pageSize, setPageSize] = useState(10);
  const [previewData, setPreviewData] = useState(null);
  const [selectedRadioId, setSelectedRadioId] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [openFeatureModal, setOpenFeatureModal] = useToggle();

  const taxonomyList = useSelector((state) => state.taxonomyList?.data);
  const taxonomyReport = useSelector((state) => state.taxonomyReport);
  const taxonomyDetails = useSelector((state) => state.taxonomyDetails?.data);
  const allAccountStats = useSelector((state) => state.allAccountStats?.data);

  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // API Call for child expansion
  const handleApiCall = useCallback((id) => {
    setSelectedRadioId(id);
    if (selectedRadioId !== id) {
      dispatch(getTaxonomyReport({ parentId: id, viewOption }));
    }
  }, [dispatch, selectedRadioId, viewOption]);

  // Initial taxonomy list fetch
  useEffect(() => {
    dispatch(getTaxonomyList());
    dispatch(getAllAccountStats());
  }, [dispatch]);

  // Main taxonomy data fetch (server search + pagination)
  useEffect(() => {
    if (selectedView === 0) {
      dispatch(getTaxonomyReport({
        offset: page,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        viewOption,
      }));
    }
  }, [dispatch, selectedView, page, pageSize, debouncedSearch, viewOption]);

  // Fetch details for selected view (non-zero selection)
  useEffect(() => {
    if (selectedView !== 0) {
      dispatch(getTaxonomyReport({ taxonomyId: selectedView, viewOption }));
      dispatch(getTaxonomyDetails({ id: selectedView }));
    }
  }, [dispatch, selectedView, viewOption]);

  // Prepare taxonomy options
  useEffect(() => {
    if (taxonomyList?.length) {
      const options = taxonomyList.map((item) => ({
        id: item.id,
        name: item.name,
        value: item.id,
      }));
      setTaxonomyOptions([defaultOption, ...options]);
    }
  }, [taxonomyList]);

  // Map level names
  const levelMap = useMemo(() => {
    if (taxonomyDetails.levels) {
      return {
        0: 'Taxonomy',
        ...Object.fromEntries(taxonomyDetails.levels.map(({ level, name }) => [level, name])),
      };
    }
    return {};
  }, [taxonomyDetails.levels]);

  // Filtered rows for client-side search (non-0 view)
  const filteredRows = useMemo(() => {
    if (selectedView === 0) return taxonomyReport.data; // server handles it
    if (!debouncedSearch.trim()) return taxonomyReport.data;
    return taxonomyReport.data.filter((row) => row.name?.toLowerCase().includes(debouncedSearch.toLowerCase()));
  }, [taxonomyReport.data, debouncedSearch, selectedView]);

  const handleExpandClose = () => {
    setExpandedReportId(null);
    setPreviewData(null);
  };

  const { columns } = useFeatureTaxonomyTable({
    allAccountStats,
    selectedView,
    levelMap,
    viewOption,
    selectedRadioId,
    setExpanded,
    setPreviewData,
    setExpandedReportId,
    handleApiCall,
    setOpenFeatureModal,
  });

  const classes = useStyles(styles)();

  return (
    <Grid container gap={2}>
      {/* Header */}
      <Grid item xs={12} className={classes.cardStyles}>
        <Stack className={classes.headerStyles}>
          <PackageHeader noPackage heading={TITLES.FEATURE_USAGE} />
          <Stack direction="row" gap={1}>
            <Button variant="outlined" onClick={() => navigate('/feature-manager')}>Manage Feature</Button>
            <Button variant="outlined" onClick={() => navigate('/taxonomy')}>Manage Taxonomy</Button>
          </Stack>
        </Stack>
      </Grid>

      {/* Left Panel */}
      {taxonomyList?.length > 0 && (
        <Grid item xs={12} md={4} className={classes.cardStyles} sx={{ minHeight: '500px' }}>
          <SelectBox title="Select View" value={selectedView} data={taxonomyOptions} onChange={(e) => setSelectedView(e.target.value)} />
          {selectedView !== 0 ? (
            <TaxonomyViewer
              selectedView={selectedView}
              selectedRadioId={selectedRadioId}
              setSelectedRadioId={setSelectedRadioId}
              expanded={expanded}
              setExpanded={setExpanded}
              handleApiCall={handleApiCall}
            />
          ) : (
            <Box sx={{ m: 2, color: '#888', fontSize: 15 }}>
              Select a view to display taxonomy data.
            </Box>
          )}
        </Grid>
      )}

      {/* Right Panel */}
      <Grid item flex={1} className={classes.cardStyles} sx={{ minHeight: '500px' }}>
        <Stack direction="row" gap={2} mb={2} maxWidth={taxonomyList?.length > 0 ? '100%' : '60%'}>
          <SearchBar
            label="Search"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            onClear={() => { setSearchQuery(''); setPage(0); }}
          />
          <SelectBox title="View Option" data={viewOptionList} value={viewOption} onChange={(e) => { setViewOption(e.target.value); setPage(0); }} />
        </Stack>

        <FeatureTaxonomyTable
          loading={taxonomyReport.loading}
          error={taxonomyReport.error}
          data={filteredRows}
          count={taxonomyReport.count}
          selectedView={selectedView}
          columns={columns}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Grid>

      {/* KPI Preview */}
      {expandedReportId !== null && (
        <KPIPreview
          title={expandedReportId.name}
          open
          previewData={previewData}
          data={previewData}
          handleClose={handleExpandClose}
          loading={!previewData}
          view={viewOption}
        />
      )}

      {openFeatureModal && (
      <FeatureLabelModal
        label="Feature Component"
        open={openFeatureModal}
        handleClose={setOpenFeatureModal}
        permissions={permissions}
      />
      )}
    </Grid>
  );
}

export default FeatureTaxonomy;
